import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { api } from "@/lib/api";
import { getWorkOSAuthUrl } from "@/lib/workos";

interface Organization {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
}

interface Company {
  id: string;
  name: string;
  workos_organization_id: string;
  default_organization: Organization;
  created_at: string;
  role: string;
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  company: Company | null;
}

interface AuthResponse {
  authenticated: boolean;
  user: User | null;
}

const API_BASE = import.meta.env.VITE_API_URL;

export function useAuth() {
  const queryClient = useQueryClient();
  const isLoggedOut = React.useRef(false);
  const redirectInProgress = React.useRef(false);

  const {
    data: authData,
    isLoading,
    error,
  } = api.useQuery<AuthResponse>("auth/me/", {
    retry: false,
    refetchOnWindowFocus: true,
    gcTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    if (!isLoading && !redirectInProgress.current && !isLoggedOut.current) {
      if (!authData?.authenticated || error) {
        redirectInProgress.current = true;
        window.location.href = getWorkOSAuthUrl();
      }
    }
    return () => {
      redirectInProgress.current = false;
    };
  }, [authData, isLoading, error]);

  const performLogout = React.useCallback(async () => {
    try {
      isLoggedOut.current = true;
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      window.location.href = `${API_BASE}/api/auth/logout/`;
    } catch (error) {
      console.error("Logout error:", error);
      isLoggedOut.current = false;
      window.location.href = getWorkOSAuthUrl();
    }
  }, [queryClient]);

  const login = React.useCallback(() => {
    window.location.href = getWorkOSAuthUrl();
  }, []);

  const logout = React.useCallback(() => {
    return performLogout();
  }, [performLogout]);

  const refreshAuth = React.useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  }, [queryClient]);

  return {
    user: authData?.user ?? null,
    isAuthenticated: !!authData?.authenticated,
    isLoading,
    error,
    login,
    logout,
    refreshAuth,
  };
}
